var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { loadWishlists, saveWishlists } from "../transport/localStorage";
import { ensureDefaultWishlist, generateId, itemsEqual, } from "../lib/wishlistHelpers";
import { appProxyAddItem, appProxyCreateWishlist, appProxyListWishlists, appProxyRemoveItem, isGuestError, } from "../transport/appProxy";
const currentCustomerId = (ctx) => { var _a, _b; return (_b = (_a = ctx.variables.variables.customer) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null; };
// Idempotent default-wishlist bootstrap.
//
// Both wishlistsGet (empty server list) and wishlistItemAdd (no target list)
// can decide a fresh customer needs a default list. Without coordination, two
// concurrent flows each POST a create and the customer ends up with duplicate
// defaults. This dedupes concurrent creation to one request per app+customer,
// and re-lists immediately before creating so a create that just landed from
// another flow (or another tab, inside the window) is reused instead of
// duplicated.
//
// We deliberately do NOT pass a name: web has no defaultWishlistName config, so
// the wishlist service names the default to match mobile (its `|| 'My Wishlist'`
// fallback). Hardcoding "My Favorites" here is what made web and mobile fork
// into two same-account default lists.
const defaultWishlistInFlight = new Map();
const ensureServerDefaultWishlist = (ctx) => {
    var _a;
    const key = `${ctx.config.appId}:${(_a = currentCustomerId(ctx)) !== null && _a !== void 0 ? _a : "guest"}`;
    const existing = defaultWishlistInFlight.get(key);
    if (existing)
        return existing;
    const cleanup = () => defaultWishlistInFlight.delete(key);
    const pending = (() => __awaiter(void 0, void 0, void 0, function* () {
        // Re-check just before creating: another flow may have created it.
        const { wishlists } = yield appProxyListWishlists(ctx.config);
        if (wishlists.length > 0)
            return wishlists[0];
        return appProxyCreateWishlist(ctx.config);
    }))().then((wishlist) => {
        cleanup();
        return wishlist;
    }, (err) => {
        cleanup();
        throw err;
    });
    defaultWishlistInFlight.set(key, pending);
    return pending;
};
// ───────────────────────────────────────────────────────────────────────────
// localStorage path (guest / fallback)
// ───────────────────────────────────────────────────────────────────────────
const wishlistsGetLocal = (handlers, ctx) => {
    var _a;
    const customerId = currentCustomerId(ctx);
    const persisted = loadWishlists(ctx.config.appId, customerId);
    const wishlists = ensureDefaultWishlist(persisted);
    if (persisted.length === 0) {
        saveWishlists(ctx.config.appId, customerId, wishlists);
    }
    ctx.variables.setWishlists(wishlists);
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, { wishlists });
};
const wishlistCreateLocal = (name, handlers, ctx) => {
    var _a;
    const customerId = currentCustomerId(ctx);
    const wishlists = loadWishlists(ctx.config.appId, customerId);
    const created = {
        id: generateId(),
        name,
        items: [],
    };
    const next = [...wishlists, created];
    saveWishlists(ctx.config.appId, customerId, next);
    ctx.variables.setWishlists(next);
    (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, created);
};
const wishlistItemAddLocal = (payload, handlers, ctx) => {
    var _a, _b, _c, _d;
    const customerId = currentCustomerId(ctx);
    const current = ensureDefaultWishlist(loadWishlists(ctx.config.appId, customerId));
    const targetId = (_a = payload.wishlistId) !== null && _a !== void 0 ? _a : (_b = current[0]) === null || _b === void 0 ? void 0 : _b.id;
    let updated;
    const next = current.map((w) => {
        if (w.id !== targetId)
            return w;
        if (w.items.some((i) => itemsEqual(i, payload))) {
            updated = w;
            return w;
        }
        updated = Object.assign(Object.assign({}, w), { items: [
                ...w.items,
                {
                    productId: payload.productId,
                    variantId: payload.variantId,
                    collectionId: payload.collectionId,
                    brandId: payload.brandId,
                },
            ] });
        return updated;
    });
    if (!updated) {
        (_c = handlers.onError) === null || _c === void 0 ? void 0 : _c.call(handlers, "WishlistDoesNotExist");
        return;
    }
    saveWishlists(ctx.config.appId, customerId, next);
    ctx.variables.setWishlists(next);
    (_d = handlers.onSuccess) === null || _d === void 0 ? void 0 : _d.call(handlers, updated);
};
const wishlistItemRemoveLocal = (payload, handlers, ctx) => {
    var _a, _b, _c, _d, _e;
    const customerId = currentCustomerId(ctx);
    const current = loadWishlists(ctx.config.appId, customerId);
    if (current.length === 0) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, "WishlistDoesNotExist");
        return;
    }
    const targetId = (_b = payload.wishlistId) !== null && _b !== void 0 ? _b : (_c = current[0]) === null || _c === void 0 ? void 0 : _c.id;
    let updated;
    const next = current.map((w) => {
        if (w.id !== targetId)
            return w;
        updated = Object.assign(Object.assign({}, w), { items: w.items.filter((i) => !itemsEqual(i, payload)) });
        return updated;
    });
    if (!updated) {
        (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, "WishlistDoesNotExist");
        return;
    }
    saveWishlists(ctx.config.appId, customerId, next);
    ctx.variables.setWishlists(next);
    (_e = handlers.onSuccess) === null || _e === void 0 ? void 0 : _e.call(handlers, updated);
};
// ───────────────────────────────────────────────────────────────────────────
// Dispatchers — switch on customer presence
// ───────────────────────────────────────────────────────────────────────────
export const wishlistsGet = (_data, handlers, ctx) => {
    if (!currentCustomerId(ctx)) {
        return wishlistsGetLocal(handlers, ctx);
    }
    ;
    (() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const result = yield appProxyListWishlists(ctx.config);
            let wishlists = result.wishlists;
            if (wishlists.length === 0) {
                // Fresh customer with no server-side wishlists: eagerly create
                // the default one. A localStorage-shaped default here would hand
                // blocks a wishlistId the server has never heard of (adds 404),
                // and an empty list strands the AddItemToWishlist drawer — its
                // Save bails client-side with "No wishlist selected" before any
                // action fires, and its Create-new path needs screen/open. A
                // real server-created default mirrors the guest bootstrap and
                // gives every surface a valid target. Goes through the dedup
                // helper so a concurrent get/add can't create a second default.
                const created = yield ensureServerDefaultWishlist(ctx);
                wishlists = [created];
            }
            ctx.variables.setWishlists(wishlists);
            (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, { wishlists });
        }
        catch (err) {
            if (isGuestError(err)) {
                return wishlistsGetLocal(handlers, ctx);
            }
            (_c = (_b = ctx.config).onError) === null || _c === void 0 ? void 0 : _c.call(_b, err, "wishlists/get");
            (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, err);
        }
    }))();
};
export const wishlistCreate = (data, handlers, ctx) => {
    var _a;
    const { name } = (data !== null && data !== void 0 ? data : {});
    if (!name || name.length < 3) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, "InvalidName");
        return;
    }
    if (!currentCustomerId(ctx)) {
        return wishlistCreateLocal(name, handlers, ctx);
    }
    ;
    (() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        try {
            const created = yield appProxyCreateWishlist(ctx.config, name);
            // Refresh full list so variables.wishlists includes the new entry.
            try {
                const { wishlists } = yield appProxyListWishlists(ctx.config);
                ctx.variables.setWishlists(wishlists);
            }
            catch (_e) {
                // Don't fail the create if the refresh fetch fails.
            }
            (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, created);
        }
        catch (err) {
            if (isGuestError(err)) {
                return wishlistCreateLocal(name, handlers, ctx);
            }
            (_c = (_b = ctx.config).onError) === null || _c === void 0 ? void 0 : _c.call(_b, err, "wishlist/create");
            (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, err);
        }
    }))();
};
export const wishlistItemAdd = (data, handlers, ctx) => {
    var _a;
    const payload = (data !== null && data !== void 0 ? data : {});
    if (!payload.productId) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, "Error");
        return;
    }
    if (!currentCustomerId(ctx)) {
        return wishlistItemAddLocal(payload, handlers, ctx);
    }
    ;
    (() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        try {
            let wishlistId = (_a = payload.wishlistId) !== null && _a !== void 0 ? _a : (_b = ctx.variables.variables.wishlists[0]) === null || _b === void 0 ? void 0 : _b.id;
            if (!wishlistId) {
                // Fresh customer with no server-side wishlists yet: create the
                // default one rather than failing. Mirrors the guest path's
                // bootstrap (which materializes a local default), but against the
                // server so the resulting id is real. Shared dedup helper so a
                // concurrent get/add doesn't create a duplicate default.
                const created = yield ensureServerDefaultWishlist(ctx);
                ctx.variables.setWishlists([created]);
                wishlistId = created.id;
            }
            const updated = yield appProxyAddItem(ctx.config, {
                wishlistId,
                productId: payload.productId,
                variantId: payload.variantId,
                collectionId: payload.collectionId,
                brandId: payload.brandId,
            });
            // Mirror the change into local variables so blocks re-render.
            ctx.variables.setWishlists(ctx.variables.variables.wishlists.map((w) => w.id === wishlistId ? updated : w));
            (_c = handlers.onSuccess) === null || _c === void 0 ? void 0 : _c.call(handlers, updated);
        }
        catch (err) {
            if (isGuestError(err)) {
                return wishlistItemAddLocal(payload, handlers, ctx);
            }
            (_e = (_d = ctx.config).onError) === null || _e === void 0 ? void 0 : _e.call(_d, err, "wishlist/item/add");
            (_f = handlers.onError) === null || _f === void 0 ? void 0 : _f.call(handlers, err);
        }
    }))();
};
export const wishlistItemRemove = (data, handlers, ctx) => {
    var _a;
    const payload = (data !== null && data !== void 0 ? data : {});
    if (!payload.productId && !payload.itemId) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, "Error");
        return;
    }
    if (!currentCustomerId(ctx)) {
        return wishlistItemRemoveLocal(payload, handlers, ctx);
    }
    ;
    (() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        const wishlistId = (_a = payload.wishlistId) !== null && _a !== void 0 ? _a : (_b = ctx.variables.variables.wishlists[0]) === null || _b === void 0 ? void 0 : _b.id;
        if (!wishlistId) {
            (_c = handlers.onError) === null || _c === void 0 ? void 0 : _c.call(handlers, "WishlistDoesNotExist");
            return;
        }
        try {
            const updated = yield appProxyRemoveItem(ctx.config, {
                wishlistId,
                itemId: payload.itemId,
                productId: payload.productId,
                variantId: payload.variantId,
            });
            ctx.variables.setWishlists(ctx.variables.variables.wishlists.map((w) => w.id === wishlistId ? updated : w));
            (_d = handlers.onSuccess) === null || _d === void 0 ? void 0 : _d.call(handlers, updated);
        }
        catch (err) {
            if (isGuestError(err)) {
                return wishlistItemRemoveLocal(payload, handlers, ctx);
            }
            (_f = (_e = ctx.config).onError) === null || _f === void 0 ? void 0 : _f.call(_e, err, "wishlist/item/remove");
            (_g = handlers.onError) === null || _g === void 0 ? void 0 : _g.call(handlers, err);
        }
    }))();
};
